import React, {
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import * as S from './SearchPanelForm.styles';
import { SearchPanelOverlay } from './SearchPanelOverlay.styles';
import { KeywordsSource } from './SearchSuggestions.types';
import { MIN_QUERY_LENGTH } from './SearchSuggestions.constants';
import { SearchRegionSelect } from './SearchRegionSelect';
import { SearchRegionDropdown } from './SearchRegionDropdown';
import { SearchSuggestions } from './SearchSuggestions';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useSearchStore } from '@/store/useSearchStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useSearch } from '@/hooks/useSearch';
import { useQueryParam } from '@/hooks/useQueryParam';
import { QUERY } from '@/constants/common';
import {
  GetSearchSuggestionResponse,
  Properties,
  SearchSuggestion,
} from '@/api/types/searchSuggestions.types';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { useDesktopCatalogueStore } from '@/store/useDesktopCatalogueStore';
import { GET_SEARCH_SUGGESTIONS } from '@/api/queries/searchSuggestions.queries';
import { GetRegionsResponse, Region } from '@/api/types/regions.types';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { usePageType } from '@/hooks/usePageType/usePageType';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import NoSsr from '@/components/NoSsr/NoSsr';
import { Portal } from '@/components/UI/Portal/Portal';
import { PageType } from '@/hooks/usePageType/usePageType.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { STORAGE_KEYS } from '@/constants/storageKeys';

export const SearchPanelForm = () => {
  const search = useSearch();
  const { isDesktop } = useBreakpoint();
  const [initialRegionLegacyId] = useQueryParam(
    `${QUERY.q}[${QUERY.region_id}]`
  );
  const [initialSearchQuery] = useQueryParam(`${QUERY.q}[${QUERY.keywords}]`);

  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);
  const [isDesktopCatalogueOpen, toggleDesktopCatalogue] =
    useDesktopCatalogueStore(state => [
      state.isDesktopCatalogueOpen,
      state.toggleDesktopCatalogue,
    ]);
  const [isSuggestionsOpened, setIsSuggestionsOpened] = useSearchStore(
    state => [state.isSuggestionsOpened, state.setIsSuggestionsOpened]
  );
  const setTopControlsCanBeFixed = usePageTopControlsStore(
    state => state.setCanBeFixed
  );
  const [isRegionDropdownOpen, toggleRegionDropdown] = useToggleOpenState();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>(
    initialSearchQuery || ''
  );
  const [regionLegacyId, setRegionLegacyId] = useState<string>(
    initialRegionLegacyId || ''
  );
  const [isSuggestionsQuerySkipped, setIsSuggestionsQuerySkipped] =
    useState(true);

  useQuery<GetSearchSuggestionResponse>(GET_SEARCH_SUGGESTIONS, {
    skip: !searchQuery.trim() || isSuggestionsQuerySkipped,
    fetchPolicy: 'no-cache',
    variables: { keywords: searchQuery.trim() },
    onCompleted: data => {
      const formattedSuggestions = data.searchSuggestions.map(item => {
        const propertiesJSON = item.properties?.replace?.(/=>/g, ':') ?? '{}';

        return {
          ...item,
          properties: JSON.parse(propertiesJSON) as Properties,
        };
      });

      toggleRegionDropdown(false);
      setSuggestions(formattedSuggestions);
      setIsSuggestionsOpened(!!formattedSuggestions.length);
      setIsSuggestionsQuerySkipped(true);
    },
  });

  const { data: { regions } = {} } = useQuery<GetRegionsResponse>(GET_REGIONS);

  const [regionsList, setRegionsList] = useState<Region[]>(regions || []);
  const initialRegionQuery = useMemo(
    () =>
      regions?.find(
        ({ legacyResourceId }) => +legacyResourceId === +regionLegacyId
      )?.name,
    [regionLegacyId, regions]
  );
  const [regionQuery, setRegionQuery] = useState<string>(
    initialRegionQuery || ''
  );
  const [isRegionSelected, setIsRegionSelected] = useState<boolean>(
    !!initialRegionLegacyId
  );
  const pageType = usePageType();
  const inputRef = useRef<HTMLInputElement>(null);
  const isFormFocused =
    isFocused || isSuggestionsOpened || isRegionDropdownOpen;
  const { t } = useTranslation();
  const isClearButtonShown = !!searchQuery && isFocused;

  function closeDropdowns() {
    setIsFocused(false);
    toggleRegionDropdown(false);
    setIsSuggestionsOpened(false);
  }

  function renderOverlay() {
    const Wrapper = isDesktop ? NoSsr : Portal;

    return (
      <Wrapper>
        <SearchPanelOverlay
          data-testid={CommonTestIds.searchPanel.overlay}
          isOpen={isSuggestionsOpened || isRegionDropdownOpen}
          onClick={closeDropdowns}
        />
      </Wrapper>
    );
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    closeDropdowns();
    search(searchQuery.trim(), {
      keywordsSource: KeywordsSource.Typewritten,
      ...(regionLegacyId && { regionLegacyId }),
    });
  }

  function onSearch(e: React.ChangeEvent<HTMLInputElement>) {
    startTransition(() => {
      setSearchQuery(e.target.value);

      if (e.target.value.length < MIN_QUERY_LENGTH) {
        setIsSuggestionsOpened(false);
      } else {
        setIsSuggestionsQuerySkipped(false);
      }
    });
  }

  function toggleDropdowns() {
    if (isNavOpen) toggleNav();
    if (isDesktopCatalogueOpen) toggleDesktopCatalogue();
  }

  function onInputFocus() {
    setIsFocused(true);

    if (suggestions.length) {
      toggleRegionDropdown(false);
      setIsSuggestionsOpened(!!searchQuery.trim());
    } else {
      setIsSuggestionsQuerySkipped(false);
    }

    toggleDropdowns();
  }

  function onInputClear(e: React.MouseEvent) {
    e.preventDefault();
    setIsFocused(true);
    setSearchQuery('');
    setIsSuggestionsOpened(false);
  }

  function onRegionSelectClick() {
    toggleRegionDropdown();
    setRegionsList(regions || []);
    setIsSuggestionsOpened(false);
    toggleDropdowns();
  }

  function onRegionSearch(value: string) {
    setRegionQuery(value);
    setRegionsList(
      regions?.filter(region =>
        region.name.toLowerCase().includes(value.toLowerCase())
      ) || []
    );

    if (!value) {
      setIsRegionSelected(false);
    }
  }

  function onRegionCloseIconClick() {
    toggleRegionDropdown(false);
    setRegionLegacyId('');
    setRegionQuery('');
    setRegionsList(regions || []);
    setIsRegionSelected(false);
    setIsSuggestionsOpened(false);
  }

  function onRegionSelect({ legacyResourceId, name }: Region) {
    toggleRegionDropdown();
    setRegionLegacyId(legacyResourceId);
    setRegionQuery(name);
    setRegionsList(regions || []);
    setIsRegionSelected(true);

    if (pageType === PageType.Search && legacyResourceId) {
      search(searchQuery, {
        keywordsSource: KeywordsSource.Typewritten,
        regionLegacyId: legacyResourceId,
      });
    }
  }

  function scrollToInput() {
    inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function onSuggestionSelect(suggestion: SearchSuggestion) {
    setSearchQuery(suggestion.value);
    setIsSuggestionsQuerySkipped(true);
    setIsSuggestionsOpened(false);
    scrollToInput();
    localStorage.setItem(STORAGE_KEYS.NEED_TO_RESTORE_SCROLL_POSITION, 'false');

    search(suggestion.value, {
      keywordsSource: KeywordsSource.Suggestion,
      ...(suggestion.properties && {
        properties: suggestion.properties,
        categoryPath: suggestion.categoryPath,
      }),
      ...(regionLegacyId && { regionLegacyId }),
    });
  }

  useEffect(() => {
    if (!isDesktop) {
      setTopControlsCanBeFixed(!isSuggestionsOpened);

      if (!isSuggestionsOpened && !searchQuery.trim()) {
        setSuggestions([]);
      }
    }
  }, [isDesktop, isSuggestionsOpened, searchQuery]);

  return (
    <S.SearchPanelFormWrapper>
      {renderOverlay()}

      <S.SearchPanelFormBox
        onSubmit={onSubmit}
        data-testid="search-panel-form-box"
      >
        <S.SearchPanelFormContent $isFocused={isFormFocused}>
          <S.SearchPanelBtn
            {...(isDesktop
              ? { onMouseDown: closeDropdowns }
              : { onClick: onSubmit, className: 'top-search__form-btn' })}
            data-testid="search-panel-btn"
          >
            <S.SearchPanelBtnIcon />
          </S.SearchPanelBtn>

          <S.SearchPanelInput
            ref={inputRef}
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={onSearch}
            onFocus={onInputFocus}
            onBlur={() => setIsFocused(false)}
            data-testid={CommonTestIds.searchPanel.input}
          />

          {isClearButtonShown && (
            <S.SearchPanelClearBtn
              onClick={onInputClear}
              onMouseDown={e => e.preventDefault()}
              data-testid="search-panel-clear-btn"
            >
              <S.SearchPanelClearBtnIcon />
            </S.SearchPanelClearBtn>
          )}

          {isDesktop && regions?.length && (
            <NoSsr>
              <SearchRegionSelect
                isRegionSelected={isRegionSelected}
                isDropdownOpen={isRegionDropdownOpen}
                value={regionQuery}
                onClick={onRegionSelectClick}
                onSearch={onRegionSearch}
                onCloseIconClick={onRegionCloseIconClick}
              >
                <SearchRegionDropdown
                  isOpen={isRegionDropdownOpen}
                  regions={regionsList}
                  regionLegacyId={regionLegacyId}
                  onSelect={onRegionSelect}
                />
              </SearchRegionSelect>
            </NoSsr>
          )}
        </S.SearchPanelFormContent>

        {isDesktop && (
          <NoSsr>
            <S.DesktopSearchPanelBtn
              className="header-btn js-header-btn header-btn_search"
              $isFormFocused={isFormFocused}
              onClick={onSubmit}
              data-testid="desktop-search-panel-btn"
            >
              {t('search.button')}
            </S.DesktopSearchPanelBtn>
          </NoSsr>
        )}
      </S.SearchPanelFormBox>

      <NoSsr>
        <SearchSuggestions
          searchQuery={searchQuery.trim()}
          suggestions={suggestions}
          onSelect={onSuggestionSelect}
        />
      </NoSsr>
    </S.SearchPanelFormWrapper>
  );
};
